import type { TWorkflowElement } from "@/types/form-workflow";
import { resolveWorkflowLaneMove } from "./workflow-drop-slot";

/**
 * Pure graph edits behind deleting a workflow step (NG-2511), kept free of the
 * store and of React so the delete dialog's "what stays / what goes" lists and
 * the store's commit read the same definitions.
 *
 * The workflow is a tree: every Review decision option and every Branch path is
 * its own lane (``parent_id`` = the node, ``branch_key`` = the option id or
 * ``"true"`` / ``"false"``), and inserting a Review or Branch mid-chain moves the
 * steps after it into its FIRST lane (``insertElementAfter``, #2557). Deleting the
 * node therefore takes every lane with it unless one lane is promoted back.
 */

/** The node itself plus every transitive descendant reachable through
 *  ``parent_id``: exactly what a cascade delete of ``rootId`` removes. */
export function workflowSubtreeIds(elements: TWorkflowElement[], rootId: string): Set<string> {
  const ids = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const el of elements) {
      if (el.parent_id && ids.has(el.parent_id) && !ids.has(el.id)) {
        ids.add(el.id);
        grew = true;
      }
    }
  }
  return ids;
}

/** The lane keys a branching node owns, in the order the canvas draws them: a
 *  Review's decision options by ``position``, a Branch's true path then its false
 *  path. Empty for a linear step (Page, Change Owner, End). */
export function outcomeLaneKeys(element: TWorkflowElement): string[] {
  if (element.kind === "branch") return ["true", "false"];
  if (element.kind === "review") {
    return [...(element.review?.decision_options ?? [])]
      .sort((a, b) => a.position - b.position)
      .map((option) => option.id);
  }
  return [];
}

/** A lane's direct steps in drawn order: ``position``, a terminal End last on a
 *  tie (the same order ``orderWorkflowElements`` lays them out in). */
function laneSteps(
  elements: TWorkflowElement[],
  parentId: string | null,
  branchKey: string | null,
): TWorkflowElement[] {
  return elements
    .filter((el) => el.parent_id === parentId && el.branch_key === branchKey)
    .sort(
      (a, b) => a.position - b.position || (a.kind === "end" ? 1 : 0) - (b.kind === "end" ? 1 : 0),
    );
}

/** Every step on one of a node's lanes plus everything nested under them: what
 *  keeping or deleting that lane keeps or deletes. The store's
 *  ``removeDecisionOption`` and both delete dialogs read lanes through it. */
export function laneSubtreeIds(
  elements: TWorkflowElement[],
  nodeId: string,
  laneKey: string,
): Set<string> {
  const ids = new Set<string>();
  for (const step of laneSteps(elements, nodeId, laneKey)) {
    for (const id of workflowSubtreeIds(elements, step.id)) ids.add(id);
  }
  return ids;
}

/** {@link laneSubtreeIds} as steps, in ``elements`` order and without the
 *  terminal Ends a lane closes with: they carry no work, so the delete dialog
 *  doesn't list them. */
export function laneSubtreeSteps(
  elements: TWorkflowElement[],
  nodeId: string,
  laneKey: string,
): TWorkflowElement[] {
  const ids = laneSubtreeIds(elements, nodeId, laneKey);
  return elements.filter((el) => ids.has(el.id) && el.kind !== "end");
}

/** A lane's direct steps split at its terminal End, which can only come last. */
function splitLaneEnd(lane: TWorkflowElement[]): {
  body: TWorkflowElement[];
  end: TWorkflowElement | null;
} {
  const last = lane.at(-1);
  return last?.kind === "end" ? { body: lane.slice(0, -1), end: last } : { body: lane, end: null };
}

/**
 * Whether the steps on one of a node's lanes can move onto another of its lanes
 * (removing a decision option but keeping its steps, NG-2649). False when the
 * source lane carries no work.
 *
 * The steps land at the destination's tail, before its End, keeping their order.
 * The source lane is already legal, so only its last step can end a lane (a
 * Review, Branch or End), which makes the block's move legal exactly when moving
 * that last step there alone would be. That is the "Move to" rule
 * (``resolveWorkflowLaneMove``), so the lane-end rules stay in one place.
 */
export function canMoveLaneSteps(
  elements: TWorkflowElement[],
  nodeId: string,
  fromLaneKey: string,
  toLaneKey: string,
): boolean {
  if (fromLaneKey === toLaneKey) return false;
  const last = splitLaneEnd(laneSteps(elements, nodeId, fromLaneKey)).body.at(-1);
  if (!last) return false;
  return resolveWorkflowLaneMove(elements, last.id, nodeId, toLaneKey) !== null;
}

/**
 * Move the steps on ``fromLaneKey`` (all but its End) to the tail of
 * ``toLaneKey``, before that lane's End, in their order and carrying their own
 * nested lanes. The source lane's End stays behind for the caller to delete with
 * the option. Positions on the destination are rewritten densely. Returns
 * ``elements`` itself when {@link canMoveLaneSteps} refuses the move.
 */
export function moveLaneSteps(
  elements: TWorkflowElement[],
  nodeId: string,
  fromLaneKey: string,
  toLaneKey: string,
): TWorkflowElement[] {
  if (!canMoveLaneSteps(elements, nodeId, fromLaneKey, toLaneKey)) return elements;
  const moved = splitLaneEnd(laneSteps(elements, nodeId, fromLaneKey)).body;
  const { body, end } = splitLaneEnd(laneSteps(elements, nodeId, toLaneKey));
  return placeOnLane(elements, [...body, ...moved, ...(end ? [end] : [])], nodeId, toLaneKey);
}

/** Put ``newLane``'s steps, in that order, on the lane (``parentId``,
 *  ``branchKey``) with dense positions (0, 1, 2 …). Every other row is untouched,
 *  so a moved step's own nested lanes ride along. */
function placeOnLane(
  elements: TWorkflowElement[],
  newLane: TWorkflowElement[],
  parentId: string | null,
  branchKey: string | null,
): TWorkflowElement[] {
  const newPosition = new Map(newLane.map((el, index) => [el.id, index]));
  return elements.map((el) => {
    const position = newPosition.get(el.id);
    if (position === undefined) return el;
    return { ...el, parent_id: parentId, branch_key: branchKey, position };
  });
}

/**
 * Delete a Review or Branch but keep the steps on one of its lanes: those steps
 * move up into the node's place on the node's own lane, in their order, carrying
 * their own nested lanes with them; every other lane is deleted with the node.
 * The inverse of what inserting the node did to the steps after it.
 *
 * Positions on the node's lane are rewritten densely (0, 1, 2 …), so callers may
 * re-sort with ``orderWorkflowElements``; the kept steps' own lanes are untouched.
 * Returns ``elements`` itself when ``nodeId`` is unknown.
 */
export function deleteKeepingLane(
  elements: TWorkflowElement[],
  nodeId: string,
  keptLaneKey: string,
): TWorkflowElement[] {
  const node = elements.find((el) => el.id === nodeId);
  if (!node) return elements;

  const kept = laneSteps(elements, node.id, keptLaneKey);
  const removed = workflowSubtreeIds(elements, node.id);
  for (const id of laneSubtreeIds(elements, node.id, keptLaneKey)) removed.delete(id);

  const siblings = laneSteps(elements, node.parent_id, node.branch_key);
  const nodeIndex = siblings.findIndex((el) => el.id === node.id);
  const newLane = [...siblings.slice(0, nodeIndex), ...kept, ...siblings.slice(nodeIndex + 1)];

  return placeOnLane(
    elements.filter((el) => !removed.has(el.id)),
    newLane,
    node.parent_id,
    node.branch_key,
  );
}
